import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import {
  QrCode, Upload, Trash2, Download, ExternalLink, RefreshCw, AlertCircle,
  Copy, Edit3, Eye, FileText, Search,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function fmtSize(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

// ---------------------------------------------------------------------------
// Single QR card — shows the QR PNG preview, the public URL, scan count and
// the row-level actions (open PDF, copy link, download PNG, rename, delete).
// ---------------------------------------------------------------------------
function QrCard({ item, onRefresh, onRename, onDelete, onReplacePdf }) {
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState(item.name || "");
  // Fetch the PNG as a blob so we can pass the JWT via axios — a raw
  // <img src="..."> tag does NOT include the Authorization header,
  // so it 401s and the QR image never renders (bug reported 2026-08-31).
  const [pngBlobUrl, setPngBlobUrl] = useState("");
  const [pngErr, setPngErr] = useState("");
  const replaceRef = useRef(null);
  const [replacing, setReplacing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objUrl = "";
    (async () => {
      try {
        const r = await axios.get(
          `${API}/qr-codes/${item.id}/png?size=300`,
          { responseType: "blob" }
        );
        objUrl = URL.createObjectURL(new Blob([r.data], { type: "image/png" }));
        if (!cancelled) setPngBlobUrl(objUrl);
      } catch (e) {
        if (!cancelled) setPngErr(e?.response?.data?.detail || e.message || "Nu am putut încărca QR-ul");
      }
    })();
    return () => {
      cancelled = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [item.id]);

  const downloadPng = async () => {
    // Authenticated fetch → blob → trigger download
    const r = await axios.get(`${API}/qr-codes/${item.id}/png?size=800`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "image/png" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `qr-${item.token}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(item.url);
    } catch {
      // Older browsers / insecure context — show a prompt as fallback
      window.prompt("Copiază URL-ul:", item.url);
    }
  };

  const submitRename = async (e) => {
    e?.preventDefault?.();
    if (!newName.trim()) return;
    await onRename(item, newName.trim());
    setRenaming(false);
  };

  const handleReplacePdf = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.pdf$/i.test(f.name) && f.type !== "application/pdf") {
      alert("Trebuie un PDF");
      return;
    }
    setReplacing(true);
    try {
      await onReplacePdf(item, f);
    } finally {
      setReplacing(false);
      if (replaceRef.current) replaceRef.current.value = "";
    }
  };

  return (
    <div
      data-testid={`qr-card-${item.id}`}
      className="group relative flex flex-col rounded-2xl border border-zinc-800 bg-gradient-to-b from-zinc-900/60 to-zinc-950/40 p-4 shadow-lg shadow-black/30 transition hover:border-orange-500/40"
    >
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          {renaming ? (
            <form onSubmit={submitRename} className="flex items-center gap-1.5">
              <input
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onBlur={submitRename}
                onKeyDown={(e) => e.key === "Escape" && setRenaming(false)}
                data-testid={`qr-rename-input-${item.id}`}
                className="flex-1 rounded-md border border-orange-500/40 bg-zinc-900 px-2 py-1 text-sm text-zinc-100 outline-none"
              />
            </form>
          ) : (
            <div className="flex items-center gap-1.5">
              <h3 className="truncate text-sm font-semibold text-zinc-100" title={item.name}>{item.name}</h3>
              <button onClick={() => setRenaming(true)} data-testid={`qr-rename-btn-${item.id}`}
                className="rounded p-1 text-zinc-600 opacity-0 transition group-hover:opacity-100 hover:text-zinc-200">
                <Edit3 className="h-3 w-3" />
              </button>
            </div>
          )}
          <p className="mt-0.5 truncate text-[10px] text-zinc-500" title={item.filename}>
            {item.filename} · {fmtSize(item.size)}
          </p>
        </div>
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-200">
          <Eye className="h-2.5 w-2.5" /> {item.scan_count || 0}
        </span>
      </div>

      <div className="mb-3 grid place-items-center rounded-xl border border-zinc-800 bg-white p-3 relative">
        {pngErr ? (
          <div className="flex h-40 w-40 items-center justify-center text-xs text-rose-500 text-center px-2">
            {pngErr}
          </div>
        ) : pngBlobUrl ? (
          <img
            src={pngBlobUrl}
            alt={`QR pentru ${item.name}`}
            className="h-40 w-40 object-contain"
            data-testid={`qr-image-${item.id}`}
          />
        ) : (
          <div className="flex h-40 w-40 items-center justify-center">
            <RefreshCw className="h-6 w-6 animate-spin text-zinc-400" />
          </div>
        )}
        {item.has_pdf === false && (
          <span className="absolute top-2 right-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-900 border border-amber-300">
            Fără PDF
          </span>
        )}
      </div>

      <div className="mb-2 truncate rounded-md border border-zinc-800 bg-zinc-900/60 px-2 py-1.5 text-[10px] text-sky-300" title={item.url}>
        {item.url}
      </div>

      <div className="mb-2 text-[10px] text-zinc-500">
        Creat {fmtDate(item.created_at)} · Ultima scanare {fmtDate(item.last_scanned_at)}
      </div>

      <div className="mt-auto flex flex-wrap gap-1.5">
        <a
          href={item.url} target="_blank" rel="noreferrer"
          data-testid={`qr-open-pdf-${item.id}`}
          className="inline-flex flex-1 items-center justify-center gap-1 rounded-md border border-sky-500/40 bg-sky-500/10 px-2 py-1.5 text-[11px] font-medium text-sky-200 hover:bg-sky-500/20"
        ><ExternalLink className="h-3 w-3" /> {item.has_pdf === false ? "Preview" : "Deschide PDF"}</a>
        <button
          type="button" onClick={copyLink}
          data-testid={`qr-copy-${item.id}`}
          className="inline-flex items-center justify-center gap-1 rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1.5 text-[11px] font-medium text-zinc-300 hover:text-zinc-100"
          title="Copiază URL-ul"
        ><Copy className="h-3 w-3" /></button>
        <button
          type="button" onClick={downloadPng}
          data-testid={`qr-download-png-${item.id}`}
          className="inline-flex items-center justify-center gap-1 rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1.5 text-[11px] font-medium text-zinc-300 hover:text-zinc-100"
          title="Descarcă PNG-ul QR-ului pentru tipărire"
        ><Download className="h-3 w-3" /></button>
        <label
          className="inline-flex cursor-pointer items-center justify-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1.5 text-[11px] font-medium text-emerald-200 hover:bg-emerald-500/20"
          title={item.has_pdf === false ? "Încarcă PDF" : "Înlocuiește PDF-ul păstrând același QR"}
          data-testid={`qr-replace-pdf-${item.id}`}
        >
          {replacing ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
          <input
            type="file" accept="application/pdf,.pdf"
            ref={replaceRef}
            onChange={handleReplacePdf}
            disabled={replacing}
            className="hidden"
          />
        </label>
        <button
          type="button" onClick={() => onDelete(item)}
          data-testid={`qr-delete-${item.id}`}
          className="inline-flex items-center justify-center gap-1 rounded-md border border-rose-500/40 bg-rose-500/10 px-2 py-1.5 text-[11px] font-medium text-rose-200 hover:bg-rose-500/20"
          title="Șterge"
        ><Trash2 className="h-3 w-3" /></button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page — list + create form on top, grid of QR cards below.
// ---------------------------------------------------------------------------
export default function QrCodes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await axios.get(`${API}/qr-codes`);
      setItems(r.data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e?.preventDefault?.();
    const f = fileRef.current?.files?.[0];
    // PDF is now OPTIONAL — user can create the QR label first (to
    // print on packaging) and upload the manual PDF later via the
    // green "replace PDF" button on the card.
    if (f && !/\.pdf$/i.test(f.name) && f.type !== "application/pdf") {
      setError("Trebuie un PDF");
      return;
    }
    setCreating(true);
    setError("");
    setInfo("");
    const fd = new FormData();
    if (f) fd.append("file", f);
    if (name.trim()) fd.append("name", name.trim());
    try {
      await axios.post(`${API}/qr-codes`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setInfo(f ? "QR code creat cu succes." : "QR code creat FĂRĂ PDF — poți încărca PDF-ul mai târziu.");
      setName("");
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setCreating(false);
    }
  };

  const handleReplacePdf = async (item, newFile) => {
    setError("");
    setInfo("");
    const fd = new FormData();
    fd.append("file", newFile);
    try {
      await axios.put(`${API}/qr-codes/${item.id}/pdf`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setInfo(`PDF actualizat pentru „${item.name}" — QR-ul rămâne același.`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  const handleRename = async (item, newName) => {
    try {
      await axios.put(`${API}/qr-codes/${item.id}`, { name: newName });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Ștergi QR codul „${item.name}"? PDF-ul nu va mai fi accesibil de la linkul curent.`)) return;
    try {
      await axios.delete(`${API}/qr-codes/${item.id}`);
      setItems((arr) => arr.filter((x) => x.id !== item.id));
      setInfo("QR șters.");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  const filtered = items.filter((it) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (it.name || "").toLowerCase().includes(q) || (it.filename || "").toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-zinc-100 flex items-center gap-2">
          <QrCode className="h-6 w-6 text-orange-400" /> Coduri QR
        </h1>
        <p className="mt-1 text-sm text-zinc-400">
          Încarcă un PDF (de ex. manual de utilizare) și primești un cod QR care, scanat de client,
          deschide direct PDF-ul. Tipărește PNG-ul pe ambalaj sau în interiorul cutiei.
        </p>
      </div>

      {/* Create form */}
      <form
        onSubmit={handleCreate}
        className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4"
        data-testid="qr-create-form"
      >
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-zinc-400">Creează cod QR nou</h2>
        <div className="grid gap-3 md:grid-cols-[1fr_2fr_auto]">
          <div>
            <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Nume (opțional)</label>
            <input
              data-testid="qr-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Manual Aspirator BRAGUS"
              className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500/50"
            />
          </div>
          <div>
            <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Fișier PDF (opțional, max 25 MB)</label>
            <input
              type="file"
              accept="application/pdf,.pdf"
              ref={fileRef}
              data-testid="qr-file-input"
              className="block w-full cursor-pointer rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-sm text-zinc-300 file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-orange-500/15 file:px-3 file:py-1.5 file:text-orange-200 hover:file:bg-orange-500/25"
            />
          </div>
          <div className="flex items-end">
            <button
              type="submit" disabled={creating}
              data-testid="qr-create-btn"
              className="inline-flex h-[38px] items-center gap-2 rounded-md border border-orange-500/50 bg-orange-500/10 px-4 text-sm font-medium text-orange-200 hover:bg-orange-500/20 disabled:opacity-50"
            >
              {creating ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {creating ? "Se încarcă..." : "Creează QR"}
            </button>
          </div>
        </div>
        {error && (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5" /> {error}
          </div>
        )}
        {info && !error && (
          <div className="mt-3 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200">
            {info}
          </div>
        )}
      </form>

      {/* List header */}
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-zinc-300">
          {items.length} cod{items.length === 1 ? "" : "uri"} QR
        </h2>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Caută..."
            data-testid="qr-search"
            className="rounded-md border border-zinc-800 bg-zinc-950 py-1.5 pl-8 pr-3 text-xs text-zinc-100 outline-none focus:border-orange-500/50"
          />
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <p className="text-sm text-zinc-500">Se încarcă...</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-800 bg-zinc-900/30 p-10 text-center text-sm text-zinc-500">
          <FileText className="mx-auto mb-3 h-8 w-8 text-zinc-700" />
          {items.length === 0
            ? "Niciun cod QR încă. Încarcă primul PDF de mai sus."
            : "Niciun cod QR nu se potrivește căutării."}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map((it) => (
            <QrCard
              key={it.id}
              item={it}
              onRefresh={load}
              onRename={handleRename}
              onDelete={handleDelete}
              onReplacePdf={handleReplacePdf}
            />
          ))}
        </div>
      )}
    </div>
  );
}
